import { useState } from "react";
import { Copy } from "@digital-twins/design-system/icons";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Text } from "@/components/ui/Text";
import { CreativeEditorSection } from "./CreativeEditorDialog";
import type { ParentBrandImportProps } from "@/types/creative-library";

export function ParentBrandImport({ parent, onApply }: ParentBrandImportProps) {
  const { t } = useTranslation("app");
  const [selected, setSelected] = useState({
    palette: true,
    typography: true,
    guidance: true,
  });
  const [applied, setApplied] = useState(false);
  return (
    <CreativeEditorSection>
      <div className="flex items-center gap-2">
        <Copy className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <Text size="sm">
          {t("creativeLibrary.importParentTitle", { name: parent.name })}
        </Text>
      </div>
      <Text size="xs" color="muted">
        {t("creativeLibrary.importParentHelp")}
      </Text>
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {(["palette", "typography", "guidance"] as const).map((field) => (
          <label
            key={field}
            className="flex min-h-11 items-center gap-2 text-sm"
          >
            <Checkbox
              checked={selected[field]}
              data-testid={`parent-import-${field}`}
              onCheckedChange={(checked) => {
                setSelected({ ...selected, [field]: checked === true });
                setApplied(false);
              }}
            />
            {t(
              `creativeLibrary.${field === "guidance" ? "brandGuidance" : field}`,
            )}
          </label>
        ))}
      </div>
      {selected.palette && parent.palette.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {parent.palette.map((color, index) => (
            <span
              key={index}
              className="h-6 w-6 rounded-full border border-border"
              style={{ backgroundColor: color.value }}
              title={`${color.name}: ${color.value}`}
            />
          ))}
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        className="gap-2 self-start"
        data-testid="parent-import-apply"
        disabled={!Object.values(selected).some(Boolean)}
        onClick={() => {
          onApply({
            ...(selected.palette
              ? { palette: parent.palette.map((color) => ({ ...color })) }
              : {}),
            ...(selected.typography ? { typography: parent.typography } : {}),
            ...(selected.guidance ? { guidance: parent.guidance } : {}),
          });
          setApplied(true);
        }}
      >
        <Copy className="h-4 w-4" />
        {t("creativeLibrary.importParentApply")}
      </Button>
      {applied && (
        <Text size="xs" color="muted" role="status">
          {t("creativeLibrary.importParentApplied")}
        </Text>
      )}
    </CreativeEditorSection>
  );
}
